/*
 * Karten-Export (Z01): Felder, Umfang und Standardauswahl – gemeinsam für Dialog und Server.
 */
export const CARD_EXPORT_FIELDS = [
  ['id', 'Karten-ID'],
  ['title', 'Titel'],
  ['board', 'Board'],
  ['column', 'Spalte'],
  ['status', 'Status'],
  ['completedAt', 'Erledigt am'],
  ['assignees', 'Zuständig'],
  ['owner', 'Hauptverantwortlich'],
  ['labels', 'Labels'],
  ['priority', 'Priorität'],
  ['start', 'Start'],
  ['due', 'Frist'],
  ['blocked', 'Blockiert'],
  ['dependsOn', 'Wartet auf'],
  ['description', 'Beschreibung'],
  ['checklist', 'Checkliste'],
  ['estimate', 'Schätzung (Std.)'],
  ['spent', 'Erfasst (Std.)'],
  ['attachments', 'Anhänge'],
  ['comments', 'Kommentare'],
  ['createdAt', 'Angelegt am'],
  ['updatedAt', 'Geändert am'],
  ['archivedAt', 'Archiviert am'],
  ['link', 'Link'],
] as const;
export type CardExportField = (typeof CARD_EXPORT_FIELDS)[number][0];
export const CARD_EXPORT_DEFAULT: CardExportField[] = ['id', 'title', 'column', 'status', 'assignees', 'labels', 'priority', 'start', 'due', 'blocked'];

export type ExportScope = 'filtered' | 'selected' | 'all';
export const EXPORT_SCOPE_TEXT: Record<ExportScope, string> = {
  filtered: 'Aktuell gefiltert',
  selected: 'Ausgewählte Karten',
  all: 'Gesamtes Board',
};

/** Tabellenzelle: Formel-Injektion verhindern (=, +, -, @, Tab, CR) und sauber quotieren */
export function csvCell(v: unknown, sep = ';'): string {
  const s = v === null || v === undefined ? '' : String(v);
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return new RegExp(`["${sep}\\n\\r]`).test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/* ---------- PDF-Berichte (Paket C) ---------- */

/** Was ein Kartenblatt enthält (Kommentare standardmäßig aus) */
export interface CardSheetOptions {
  checklist: boolean;
  dependencies: boolean;
  /** Titelbild und Bildanhänge als Vorschau einbetten */
  images: boolean;
  attachments: boolean;
  comments: boolean;
}
export const CARD_SHEET_DEFAULT: CardSheetOptions = { checklist: true, dependencies: true, images: false, attachments: true, comments: false };
export const CARD_SHEET_TEXT: Record<keyof CardSheetOptions, string> = {
  checklist: 'Checkliste',
  dependencies: 'Abhängigkeiten',
  images: 'Titelbild und Bilder (Vorschau)',
  attachments: 'Anhänge als Dateiliste',
  comments: 'Kommentare mit Autor und Datum',
};

export type ReportLayout = 'table' | 'sheets';
export const REPORT_LAYOUT_TEXT: Record<ReportLayout, string> = { table: 'Aufgabenliste (Tabelle)', sheets: 'Ausführliche Kartenblätter' };

/** Startgrenzen (E05): nie still abschneiden */
export const REPORT_LIMITS = { cards: 1000, pages: 200, megabytes: 50, tableFieldsPortrait: 7, tableFieldsLandscape: 11 } as const;

export type ExportJobStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled' | 'expired';
export const EXPORT_STATUS_TEXT: Record<ExportJobStatus, string> = {
  queued: 'Wartet',
  running: 'Wird erstellt',
  done: 'Fertig',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen',
  expired: 'Abgelaufen',
};

export interface ReportTemplate {
  id: string;
  kind: 'board' | 'dashboard';
  name: string;
  targetId: string;
  config: Record<string, unknown>;
  updatedAt: string;
}

export interface ExportJob {
  id: string;
  kind: 'card' | 'board' | 'dashboard';
  title: string;
  status: ExportJobStatus;
  error: string | null;
  pages: number | null;
  size: number | null;
  createdAt: string;
  finishedAt: string | null;
  expiresAt: string | null;
  /** Download gesperrt, weil eine Quelle nicht mehr zugänglich ist */
  blocked?: string | null;
}

/* ---------- Medienübersicht (Z02) ---------- */
export type MediaKind = 'all' | 'image' | 'av' | 'pdf' | 'doc';
export const MEDIA_KIND_TEXT: Record<MediaKind, string> = { all: 'Alle', image: 'Bilder', av: 'Audio & Video', pdf: 'PDF', doc: 'Dokumente' };
export type MediaSort = 'new' | 'old' | 'name';
export const MEDIA_SORT_TEXT: Record<MediaSort, string> = { new: 'Neueste zuerst', old: 'Älteste zuerst', name: 'Dateiname A–Z' };
